import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { RefreshCw, Activity, Clock, Users, CheckCircle2, Search, Phone, CalendarDays, XCircle } from "lucide-react";
import { getLiveQueue, getMyBooking } from "../lib/api";
import { Reveal } from "../components/Motion";
import { Input } from "../components/ui/input";
import { formatTimeAmPm } from "../lib/timeUtils";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const today = new Date().toISOString().slice(0, 10);

export default function LiveQueue() {
  const [phone, setPhone] = useState("");
  const [date, setDate] = useState(today);
  const [searched, setSearched] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [queueLoading, setQueueLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [booking, setBooking] = useState(null);
  const [queueData, setQueueData] = useState(null);
  const [statusError, setStatusError] = useState("");
  const timer = useRef(null);

  const phoneValid = phone.replace(/\D/g, "").length === 10;
  const hasArrived = booking?.status === "ARRIVED";

  const loadQueue = async (scheduleId, isRefresh) => {
    if (!scheduleId) return;
    if (isRefresh) setRefreshing(true);
    setQueueLoading(true);
    try {
      setQueueData(await getLiveQueue(scheduleId));
    } catch (error) {
      toast.error("Could not load the live queue. Please try again.");
    } finally {
      setQueueLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!hasArrived || !booking?.scheduleId) return;
    loadQueue(booking.scheduleId, false);
    timer.current = setInterval(() => loadQueue(booking.scheduleId, true), 6000);
    return () => clearInterval(timer.current);
  }, [hasArrived, booking?.scheduleId]);

  const handleSearch = async () => {
    if (!phoneValid) return;
    setSearched(true);
    setStatusError("");
    setStatusLoading(true);
    setBooking(null);
    setQueueData(null);

    try {
      const foundBooking = await getMyBooking(phone, date);
      const selectedBooking = Array.isArray(foundBooking) ? foundBooking[0] ?? null : foundBooking ?? null;
      if (!selectedBooking) {
        setStatusError("No booking found for this mobile number and date.");
        return;
      }
      setBooking(selectedBooking);
    } catch (error) {
      if (error?.response?.status === 404) {
        setStatusError("No booking found for this mobile number and date.");
      } else {
        setStatusError("Something went wrong. Please try again.");
      }
    } finally {
      setStatusLoading(false);
    }
  };

  const nowServing = queueData?.current?.tokenNo ?? null;
  const yourToken = booking?.token ?? booking?.tokenNo ?? null;
  const waitingTokens = Array.isArray(queueData?.waiting) ? queueData.waiting.map((p) => p.tokenNo) : [];
  const waitingPosition = yourToken ? waitingTokens.indexOf(yourToken) : -1;
  const doctorName = queueData?.doctorName ?? booking?.doctorName;
  const clinicName = queueData?.clinicName ?? booking?.clinicName;
  const averageMinutes = queueData?.averageMinutesPerPatient ?? 5;

  const bookingStatus = booking?.status?.toUpperCase() ?? "UNKNOWN";
  const statusMessages = {
    BOOKED: {
      title: "Your appointment is booked.",
      message: "The session has not started yet. Please wait for the doctor to begin the clinic session or check with reception for the schedule.",
    },
    WALK_IN_PENDING: {
      title: "Walk-in request is pending.",
      message: "Your walk-in registration is complete. Please wait for reception or the doctor to begin the session.",
    },
    NOT_STARTED: {
      title: "The session has not started yet.",
      message: "Please wait for the clinic session to begin or check with reception for the next available slot.",
    },
    SCHEDULED: {
      title: "Your session is scheduled.",
      message: "The clinic session has not started yet. Please wait or confirm the schedule with reception.",
    },
    ARRIVED: {
      title: "You have arrived.",
      message: "Your live queue details are now available. Refresh or wait for updates.",
    },
    CONSULTING: {
      title: "You are currently in consultation.",
      message: "The live queue is not available while you are consulting. Please wait until your consultation is complete.",
    },
    COMPLETED: {
      title: "Your consultation is complete.",
      message: "Thank you for visiting. If you need further assistance, please contact reception.",
    },
    CANCELLED: {
      title: "Your appointment is cancelled.",
      message: "This appointment has been cancelled. Please contact reception to rebook if needed.",
    },
    NO_SHOW: {
      title: "You missed your appointment.",
      message: "Please check in at reception if you still wish to be seen by the doctor.",
    },
    UNKNOWN: {
      title: "Status unavailable.",
      message: "Please check with reception for the latest appointment status.",
    },
  };
  const statusStyles = {
    BOOKED: "bg-primary/10 text-primary border-primary/20",
    WALK_IN_PENDING: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    NOT_STARTED: "bg-slate-500/10 text-slate-200 border-slate-500/20",
    SCHEDULED: "bg-slate-500/10 text-slate-200 border-slate-500/20",
    ARRIVED: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
    CONSULTING: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    COMPLETED: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
    CANCELLED: "bg-rose-500/10 text-rose-500 border-rose-500/20",
    NO_SHOW: "bg-rose-500/10 text-rose-500 border-rose-500/20",
    UNKNOWN: "bg-muted/20 text-muted-foreground border-border",
  };
  const statusInfo = statusMessages[bookingStatus] ?? statusMessages.UNKNOWN;
  const statusClassName = statusStyles[bookingStatus] ?? statusStyles.UNKNOWN;

  const position = queueData
    ? yourToken === nowServing
      ? 0
      : yourToken === queueData?.next?.tokenNo
      ? 1
      : waitingPosition >= 0
      ? waitingPosition + (queueData?.current ? (queueData?.next ? 2 : 1) : 1)
      : null
    : null;
  const estimatedWait = queueData?.estimatedTotalWaitMinutes ?? (position !== null ? position * averageMinutes : null);

  const state = position === 0 ? "serving" : position === 1 ? "next" : position > 1 ? "waiting" : "waiting";
  const headline = {
    serving: "It's your turn.",
    next: "You're next in queue.",
    waiting: "Your appointment is being prepared.",
  }[state];
  const sub = {
    serving: "Please proceed to the consultation room.",
    next: "Please be ready — you'll be called shortly.",
    waiting:
      position !== null && position > 1
        ? `${position} patients ahead of you.`
        : "Your status is being updated. Please check with reception.",
  }[state];

  const formatToken = (token) => (token ? `#${token}` : "—");
  const positionLabel = position === null ? "—" : state === "serving" ? "Now" : `${position} ahead`;
  const waitLabel = state === "serving" ? "0 min" : estimatedWait ? `~${estimatedWait} min` : "—";

  return (
    <div className="relative min-h-screen pt-28 pb-24">
      <div className="glow-teal pointer-events-none absolute top-20 right-0 h-[420px] w-[560px] blur-[120px] opacity-50" />
      <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">Live queue</p>
          <div className="mt-3 flex items-end justify-between gap-4 flex-wrap">
            <h1 className="font-display text-4xl sm:text-5xl font-medium text-foreground leading-tight">Track your turn, in real time.</h1>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 glass gradient-border rounded-[2rem] p-8 sm:p-12">
            <div className="grid lg:grid-cols-[1.1fr_auto] gap-8 items-start">
              <div>
                <p className="text-lg text-foreground font-semibold">Enter your mobile number and appointment date.</p>
                <p className="mt-2 text-muted-foreground">We only show live queue details after your arrival has been marked at reception.</p>
              </div>
              <div className="grid gap-4">
                <div>
                  <Label className="text-muted-foreground">Mobile number</Label>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="Enter your 10-digit mobile number" inputMode="numeric" maxLength={10}
                    className="mt-2 h-12 rounded-xl bg-muted/50 border-border text-foreground" />
                </div>
                <div>
                  <Label className="text-muted-foreground">Appointment date</Label>
                  <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                    className="mt-2 h-12 rounded-xl bg-muted/50 border-border text-foreground" />
                </div>
                <button onClick={handleSearch} disabled={!phoneValid || statusLoading}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-white font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50">
                  {statusLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search
                </button>
              </div>
            </div>

            {statusError ? (
              <div className="mt-6 rounded-3xl bg-rose-500/10 border border-rose-500/20 p-5 text-rose-700">
                {statusError}
              </div>
            ) : null}

            {searched && booking && (
              <div className="mt-6 grid gap-4 rounded-[2rem] border border-border bg-surface/80 p-5 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-3xl bg-muted/70 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Doctor</p>
                  <p className="mt-2 text-lg font-semibold text-foreground">{doctorName ?? "Unknown"}</p>
                </div>
                <div className="rounded-3xl bg-muted/70 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Clinic</p>
                  <p className="mt-2 text-lg font-semibold text-foreground">{clinicName ?? "Unknown"}</p>
                </div>
                <div className="rounded-3xl bg-muted/70 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Appointment</p>
                  <p className="mt-2 text-lg font-semibold text-foreground">{formatTimeAmPm(booking.slot ?? booking.slotTime ?? "")}</p>
                </div>
                <div className="rounded-3xl bg-muted/70 p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Status</p>
                      <p className="mt-2 text-lg font-semibold text-foreground">{booking.status ?? "Unknown"}</p>
                    </div>
                    <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusClassName}`}>
                      {bookingStatus}
                    </span>
                  </div>
                  <p className="mt-4 text-sm text-muted-foreground">Token {formatToken(yourToken)}</p>
                </div>
              </div>
            )}

            {searched && booking && !hasArrived && (
              <div className="mt-6 rounded-3xl bg-amber-500/10 border border-amber-500/20 p-5 text-amber-900">
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5" />
                  <div>
                    <p className="font-semibold">{statusInfo.title}</p>
                    <p className="mt-1 text-sm text-foreground/80">{statusInfo.message}</p>
                  </div>
                </div>
                <p className="mt-4 text-sm text-muted-foreground">Current status: {booking.status ?? "Unknown"}</p>
              </div>
            )}

            {hasArrived ? (
              <div className="mt-10">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">Live queue</p>
                    <h2 className="mt-3 font-display text-4xl sm:text-5xl font-medium text-foreground leading-tight">{headline}</h2>
                    <p className="mt-3 text-muted-foreground text-lg">{sub}</p>
                    <p className="mt-2 text-sm text-muted-foreground/70">{queueData?.doctorName} · {queueData?.clinicName}</p>
                  </div>
                  <button onClick={() => loadQueue(booking.scheduleId, true)}
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-surface/80 px-5 py-3 text-foreground hover:bg-surface transition-colors">
                    <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
                  </button>
                </div>

                <div className="mt-8 glass gradient-border rounded-[2rem] p-8 sm:p-12">
                  {queueLoading || !queueData ? (
                    <div className="animate-pulse space-y-4">
                      <div className="h-4 w-32 bg-muted rounded" />
                      <div className="h-10 w-72 bg-muted rounded" />
                    </div>
                  ) : (
                    <div className="grid lg:grid-cols-2 gap-10 items-center">
                      <div>
                        <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                          <span className="relative inline-flex text-primary"><span className="pulse-ring h-2 w-2 rounded-full bg-primary relative" /></span>
                          Live · updates every 6s
                        </span>
                        <h2 className="mt-5 font-display text-4xl sm:text-5xl font-medium text-foreground leading-tight">{headline}</h2>
                        <p className="mt-3 text-muted-foreground text-lg">{sub}</p>
                        <p className="mt-2 text-sm text-muted-foreground/70">{queueData.doctorName} · {queueData.clinicName}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <Stat icon={Activity} label="Now serving" value={formatToken(queueData.nowServing)} accent />
                        <Stat icon={CheckCircle2} label="Your token" value={formatToken(yourToken)} />
                        <Stat icon={Users} label="Position" value={positionLabel} />
                        <Stat icon={Clock} label="Est. wait" value={waitLabel} />
                      </div>
                    </div>
                  )}
                </div>

                {queueData && (
                  <Reveal delay={0.15}>
                    <div className="mt-8 glass rounded-3xl p-8">
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>Queue progress</span>
                        <span>{queueData.nowServing} / {queueData.totalInQueue} seen</span>
                      </div>
                      <div className="mt-3 h-3 rounded-full bg-muted overflow-hidden">
                        <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(100, (queueData.nowServing / queueData.totalInQueue) * 100)}%` }}
                          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} className="h-full aurora-gradient" />
                      </div>
                      <p className="mt-4 text-muted-foreground">
                        The queue moves at roughly <b className="text-foreground">{queueData.averageMinutesPerPatient} min</b> per patient. We'll keep this updated automatically.
                      </p>
                    </div>
                  </Reveal>
                )}
              </div>
            ) : null}
          </div>
        </Reveal>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl p-5 border ${accent ? "aurora-gradient text-white border-transparent" : "bg-muted/50 border-border text-foreground"}`}>
      <Icon className={`h-5 w-5 ${accent ? "text-white/85" : "text-primary"}`} />
      <p className={`mt-3 text-xs uppercase tracking-[0.15em] ${accent ? "text-white/80" : "text-muted-foreground"}`}>{label}</p>
      <p className="mt-1 font-display text-3xl font-medium">{value}</p>
    </div>
  );
}
